import React, { useState, useEffect } from 'react';
import { ToggleLeft, ToggleRight, Star, Filter, Trash2, CheckSquare, Square, Edit, X } from 'lucide-react';
import * as api from '../services/api';
import CategoryMultiSelect from '../components/CategoryMultiSelect';

const isCustomer = () => JSON.parse(localStorage.getItem('user') || '{}')?.role === 'customer';

function Channels() {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    category: '',
    logo: '',
    epg_channel_id: '',
    sort_order: 0
  });
  const [pagination, setPagination] = useState({ limit: (() => { try { const n = parseInt(sessionStorage.getItem('iptv-page-size') || '50', 10); return [50, 100, 200].includes(n) ? n : 50; } catch { return 50; } })(), offset: 0, total: 0 });
  const [filters, setFilters] = useState({
    provider_id: '',
    categories: [],
    enabled: '',
    search: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  // Reload when provider filter changes
  useEffect(() => {
    if (filters.provider_id) {
      setLoading(true);
      loadData();
    }
  }, [filters.provider_id]);

  // Reload when page changes
  useEffect(() => {
    setLoading(true);
    loadData();
  }, [pagination.offset, pagination.limit]);

  const loadData = async () => {
    try {

      // Remove empty filters; categories[] → comma-separated for API (Issue 117)
      const cleanFilters = Object.fromEntries(
        Object.entries(filters)
          .filter(([key, value]) => {
            if (key === 'categories') return Array.isArray(value) && value.length > 0;
            return value !== '';
          })
          .map(([key, value]) =>
            key === 'categories' ? ['categories', value.join(',')] : [key, value]
          )
      );

      const categoriesPromise = isCustomer() ? api.customerGetCategories('channels', filters.provider_id || undefined) : api.getChannelCategories(filters.provider_id ? { provider_id: filters.provider_id } : {});
      const providersPromise = isCustomer() ? api.customerGetProfile().then(r => ({ data: (r.data.provider_lines || []).map(pl => ({ id: pl.id, name: pl.name })) })).catch(() => ({ data: [] })) : api.getProviders();
      const channelsPromise = isCustomer()
        ? api.customerGetChannels({ ...cleanFilters, limit: pagination.limit, offset: pagination.offset })
        : api.getChannels({ ...cleanFilters, limit: pagination.limit, offset: pagination.offset });

      const [channelsRes, providersRes, categoriesRes] = await Promise.all([
        channelsPromise,
        providersPromise,
        categoriesPromise
      ]);
      const channelsData = channelsRes.data?.data || channelsRes.data;
      setChannels(Array.isArray(channelsData) ? channelsData : []);
      setPagination(prev => ({ ...prev, total: channelsRes.data?.total || (Array.isArray(channelsData) ? channelsData.length : 0) }));
      setProviders(Array.isArray(providersRes.data) ? providersRes.data : []);
      setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);
      setSelectedIds([]);
      if (isCustomer()) {
        try {
          const favRes = await api.customerGetFavorites();
          setFavoriteIds(new Set((favRes.data || []).filter(f => f.item_type === 'channel').map(f => f.item_id)));
        } catch (e) { setFavoriteIds(new Set()); }
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      setChannels([]);
      setProviders([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = async (key, value) => {
    const newFilters = { ...filters, [key]: value };

    // If provider changes, reset category and reload categories for that provider
    if (key === 'provider_id') {
      newFilters.categories = [];
      setFilters(newFilters);

      // Load categories for the selected provider
      try {
        const categoriesRes = isCustomer()
          ? await api.customerGetCategories('channels', value || undefined)
          : await api.getChannelCategories(value ? { provider_id: value } : {});
        setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);
      } catch (error) {
        console.error('Failed to load categories:', error);
        setCategories([]);
      }
    } else {
      setFilters(newFilters);
    }
  };

  const handlePageSizeChange = (n) => {
    const limit = parseInt(n, 10);
    try { sessionStorage.setItem('iptv-page-size', String(limit)); } catch (_) {}
    setLoading(true);
    setPagination(prev => ({ ...prev, limit, offset: 0 }));
  };

  const applyFilters = () => {
    setLoading(true);
    setPagination(prev => ({ ...prev, offset: 0 }));
    loadData();
  };



  const toggleFavorite = async (id) => {
    if (!isCustomer()) return;
    try {
      const res = await api.customerToggleFavorite('channel', id);
      setFavoriteIds(prev => {
        const next = new Set(prev);
        if (res.data.favorited) next.add(id); else next.delete(id);
        return next;
      });
    } catch (e) { alert('Favorite failed: ' + (e.response?.data?.error || e.message)); }
  };
  const toggleChannel = async (id, currentStatus) => {
    try {
      if (isCustomer()) {
        await api.customerToggleChannel(id);
      } else {
        await api.updateChannel(id, { enabled: currentStatus ? 0 : 1 });
      }
      loadData();
    } catch (error) {
      alert('Failed to toggle channel: ' + (error.response?.data?.error || error.message));
    }
  };

  const toggleSelection = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === channels.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(channels.map(c => c.id));
    }
  };

  const bulkEnableSelected = async () => {
    if (selectedIds.length === 0) {
      alert('Please select channels first');
      return;
    }
    if (!window.confirm(`Enable ${selectedIds.length} selected channel(s)?`)) return;

    try {
      await isCustomer() ? api.customerBulkToggleChannels(selectedIds, true) : api.bulkUpdateChannels({ channel_ids: selectedIds, updates: { enabled: 1 } });
      loadData();
    } catch (error) {
      alert('Bulk enable failed: ' + (error.response?.data?.error || error.message));
    }
  };

  const bulkDisableSelected = async () => {
    if (selectedIds.length === 0) {
      alert('Please select channels first');
      return;
    }
    if (!window.confirm(`Disable ${selectedIds.length} selected channel(s)?`)) return;

    try {
      await isCustomer() ? api.customerBulkToggleChannels(selectedIds, false) : api.bulkUpdateChannels({ channel_ids: selectedIds, updates: { enabled: 0 } });
      loadData();
    } catch (error) {
      alert('Bulk disable failed: ' + (error.response?.data?.error || error.message));
    }
  };

  const deleteChannel = async (id, name) => {
    if (!window.confirm(`Delete channel "${name}"?`)) return;
    try {
      await api.deleteChannel(id);
      loadData();
    } catch (error) {
      alert('Delete failed: ' + (error.response?.data?.error || error.message));
    }
  };

    const enableProviderContent = async (providerId, providerName, enable) => {
    const action = enable ? 'Enable' : 'Disable';
    if (!window.confirm(`${action} ALL channels from provider "${providerName}"?`)) return;
    try {
      if (isCustomer()) {
        await api.customerBulkToggleChannelsByFilter({ enabled: enable, provider_id: providerId });
      } else {
        if (enable) { await api.enableProviderContent(providerId); }
        else { await api.disableProviderContent(providerId); }
      }
      alert(`${action}d all channels from ${providerName}`);
      loadData();
    } catch (error) { alert(`Provider ${action.toLowerCase()} failed: ` + (error.response?.data?.error || error.message)); }
  };

  const bulkDeleteSelected = async () => {
    if (selectedIds.length === 0) {
      alert('Please select channels first');
      return;
    }

    if (!window.confirm(`Delete ${selectedIds.length} selected channels?`)) {
      return;
    }

    try {
      if (isCustomer()) {
        await api.customerBulkToggleChannels(selectedIds.map(String), false);
        alert('Channels removed from your view');
      } else {
        await api.bulkDeleteChannels({ channel_ids: selectedIds });
      }
      loadData();
    } catch (error) {
      alert('Bulk delete failed: ' + (error.response?.data?.error || error.message));
    }
  };

    const bulkEnableByCategory = async () => {
    if (!filters.categories.length) { alert('Please select at least one category first'); return; }
    if (!window.confirm(`Enable all channels in ${filters.categories.length} selected categor${filters.categories.length === 1 ? "y" : "ies"}?`)) return;
    try {
      if (isCustomer()) {
        await api.customerBulkToggleChannelsByFilter({ enabled: true, categories: filters.categories, ...(filters.provider_id ? { provider_id: filters.provider_id } : {}) });
      } else {
        const params = { categories: filters.categories.join(',') };
        if (filters.provider_id) { params.provider_id = filters.provider_id; }
        const res = await api.bulkEnableChannels(params);
        alert(res.data.message);
      }
      loadData();
    } catch (error) { alert('Bulk enable failed: ' + (error.response?.data?.error || error.message)); }
  };

    const bulkDisableByCategory = async () => {
    if (!filters.categories.length) { alert('Please select at least one category first'); return; }
    if (!window.confirm(`Disable all channels in ${filters.categories.length} selected categor${filters.categories.length === 1 ? "y" : "ies"}?`)) return;
    try {
      if (isCustomer()) {
        await api.customerBulkToggleChannelsByFilter({ enabled: false, categories: filters.categories, ...(filters.provider_id ? { provider_id: filters.provider_id } : {}) });
      } else {
        const params = { categories: filters.categories.join(',') };
        if (filters.provider_id) { params.provider_id = filters.provider_id; }
        const res = await api.bulkDisableChannels(params);
        alert(res.data.message);
      }
      loadData();
    } catch (error) { alert('Bulk disable failed: ' + (error.response?.data?.error || error.message)); }
  };

  const openEditModal = (channel) => {
    setEditingChannel(channel);
    setEditForm({
      name: channel.name,
      category: channel.category || '',
      logo: channel.logo || '',
      epg_channel_id: channel.epg_channel_id || '',
      sort_order: channel.sort_order || 0
    });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingChannel(null);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.updateChannel(editingChannel.id, editForm);
      closeEditModal();
      loadData();
    } catch (error) {
      alert('Failed to update channel: ' + (error.response?.data?.error || error.message));
    }
  };

  const nextPage = () => {
    setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }));
  };

  const prevPage = () => {
    setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }));
  };

  if (loading) {
    return <div className="loading">Loading channels...</div>;
  }

  return (
    <div>
      <div className="page-header">
        <h2>Channels</h2>
        <p>Manage your aggregated IPTV channels</p>
      </div>

      <div className="table-container" style={{ marginBottom: '20px' }}>
        <div className="table-header">
          <h3>Filters</h3>
        </div>
        <div style={{ padding: '20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>Provider</label>
            <select
              value={filters.provider_id}
              onChange={(e) => handleFilterChange('provider_id', e.target.value)}
              style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
            >
              <option value="">All Providers</option>
              {providers.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          <div>
            <CategoryMultiSelect
              categories={categories}
              selected={filters.categories}
              onChange={(next) => handleFilterChange('categories', next)}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>Status</label>
            <select
              value={filters.enabled}
              onChange={(e) => handleFilterChange('enabled', e.target.value)}
              style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
            >
              <option value="">All</option>
              <option value="true">Enabled</option>
              <option value="false">Disabled</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>Search</label>
            <input
              type="text"
              placeholder="Search channels..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button className="btn btn-primary" onClick={applyFilters} style={{ width: '100%' }}>
              <Filter size={16} />
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedIds.length > 0 && (
        <div className="table-container" style={{ marginBottom: '20px', background: '#f0f9ff', border: '1px solid #0ea5e9' }}>
          <div style={{ padding: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong>{selectedIds.length} channel(s) selected</strong>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button className="btn btn-success" onClick={bulkEnableSelected}>
                Enable Selected
              </button>
              <button className="btn btn-secondary" onClick={bulkDisableSelected}>
                Disable Selected
              </button>
              <button className="btn btn-danger" onClick={bulkDeleteSelected}>
                <Trash2 size={16} />
                Delete Selected
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Bulk Actions */}
      {filters.categories.length > 0 && (
        <div className="table-container" style={{ marginBottom: '20px', background: '#fef3c7', border: '1px solid #f59e0b' }}>
          <div style={{ padding: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong>Bulk actions for {filters.categories.length} categor{filters.categories.length === 1 ? 'y' : 'ies'}: {filters.categories.join(', ')}</strong>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button className="btn btn-success" onClick={bulkEnableByCategory}>
                Enable All in Category
              </button>
              <button className="btn btn-secondary" onClick={bulkDisableByCategory}>
                Disable All in Category
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Provider Bulk Actions */}
      {filters.provider_id && (
        <div className="table-container" style={{ marginBottom: '20px', background: '#e0e7ff', border: '1px solid #6366f1' }}>
          <div style={{ padding: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong>Bulk actions for provider: {providers.find(p => p.id === parseInt(filters.provider_id))?.name || filters.provider_id}</strong>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button className="btn btn-success" onClick={() => enableProviderContent(filters.provider_id, providers.find(p => p.id === parseInt(filters.provider_id))?.name || '', true)}>
                Enable All Provider Channels
              </button>
              <button className="btn btn-secondary" onClick={() => enableProviderContent(filters.provider_id, providers.find(p => p.id === parseInt(filters.provider_id))?.name || '', false)}>
                Disable All Provider Channels
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="table-container">
        <div className="table-header">
          <h3>Channels ({pagination.total})</h3>
        </div>

        {channels.length === 0 ? (
          <div className="empty-state">
            <h3>No channels found</h3>
            <p>Sync a provider to see channels here</p>
          </div>
        ) : (
          <>
          <table>
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  <button
                    onClick={toggleSelectAll}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                  >
                    {selectedIds.length === channels.length ? (
                      <CheckSquare size={20} color="#0ea5e9" />
                    ) : (
                      <Square size={20} color="#9ca3af" />
                    )}
                  </button>
                </th>
                <th>Channel</th>
                <th>Category</th>
                <th>Provider</th>
                <th>EPG</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {channels.map((channel) => (
                <tr key={channel.id}>
                  <td>
                    <button
                      onClick={() => toggleSelection(channel.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                      {selectedIds.includes(channel.id) ? (
                        <CheckSquare size={20} color="#0ea5e9" />
                      ) : (
                        <Square size={20} color="#9ca3af" />
                      )}
                    </button>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {channel.logo && (
                        <img src={channel.logo} alt="" style={{ width: '30px', height: '30px', borderRadius: '4px' }} />
                      )}
                      {isCustomer() && (
                        <button onClick={() => toggleFavorite(channel.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }} title="Add to My List">
                          <Star size={16} fill={favoriteIds.has(channel.id) ? '#f59e0b' : 'none'} color={favoriteIds.has(channel.id) ? '#f59e0b' : '#9ca3af'} />
                        </button>
                      )}
                      <strong>{channel.name}</strong>
                    </div>
                  </td>
                  <td>{channel.category || 'Uncategorized'}</td>
                  <td>{channel.provider_name}</td>
                  <td>
                    {channel.has_epg ? (
                      <span className="badge badge-success">Mapped</span>
                    ) : (
                      <span className="badge badge-warning">No EPG</span>
                    )}
                  </td>
                  <td>
                    <span className={`badge badge-${channel.enabled ? 'success' : 'danger'}`}>
                      {channel.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button
                        onClick={() => openEditModal(channel)}
                        className="btn btn-secondary"
                        style={{ padding: '6px 12px' }}
                        title="Edit Channel"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => toggleChannel(channel.id, channel.enabled)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px'
                        }}
                      >
                        {channel.enabled ? (
                          <ToggleRight size={24} color="#10b981" />
                        ) : (
                          <ToggleLeft size={24} color="#9ca3af" />
                        )}
                      </button>
                      <button
                        onClick={() => deleteChannel(channel.id, channel.name)}
                        className="btn btn-danger btn-sm"
                        title="Delete Channel"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

            {/* Pagination */}
            <div style={{ padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #e5e7eb' }}>
              <div>
                <label style={{ fontSize: '13px', color: '#6b7280', marginRight: '12px' }}>
                  Per page{' '}
                  <select
                    value={pagination.limit}
                    onChange={(e) => handlePageSizeChange(e.target.value)}
                    style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                  >
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                  </select>
                </label>
                Showing {pagination.offset + 1} - {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total}
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  className="btn btn-secondary"
                  onClick={prevPage}
                  disabled={pagination.offset === 0}
                >
                  Previous
                </button>
                <div style={{ padding: '8px 16px', background: '#f3f4f6', borderRadius: '6px' }}>
                  Page {Math.floor(pagination.offset / pagination.limit) + 1} of {Math.ceil(pagination.total / pagination.limit) || 1}
                </div>
                <button
                  className="btn btn-secondary"
                  onClick={nextPage}
                  disabled={pagination.offset + pagination.limit >= pagination.total}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Edit Channel Modal */}
      {showEditModal && editingChannel && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: 'white', padding: '30px', borderRadius: '8px',
            width: '100%', maxWidth: '600px', maxHeight: '90vh', overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h3>Edit Channel: {editingChannel.name}</h3>
              <button onClick={closeEditModal} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="form-group">
                <label>Channel Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Category</label>
                <input
                  type="text"
                  value={editForm.category}
                  onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                  list="categories-list"
                />
                <datalist id="categories-list">
                  {categories.map(cat => <option key={cat} value={cat} />)}
                </datalist>
              </div>
              <div className="form-group">
                <label>Logo URL</label>
                <input
                  type="text"
                  value={editForm.logo}
                  onChange={(e) => setEditForm({ ...editForm, logo: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Sort Order</label>
                <input
                  type="number"
                  value={editForm.sort_order}
                  onChange={(e) => setEditForm({ ...editForm, sort_order: parseInt(e.target.value) })}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                  Save Changes
                </button>
                <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={closeEditModal}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Channels;
